import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  ThemeProvider,
  createTheme,
  Chip,
  Drawer,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SettingsIcon from "@mui/icons-material/Settings";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CakeIcon from "@mui/icons-material/Cake";
import PushPinIcon from "@mui/icons-material/PushPin";
import ShareIcon from "@mui/icons-material/Share";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import MicIcon from "@mui/icons-material/MicRounded";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import React, { useState, useEffect } from "react";
import { useDataProvider } from "react-admin";
import { TabbedAnalyticsDashboard } from "../analytics/mm/components/TabbedAnalyticsDashboard";
import { DashboardAiProvider } from "../analytics/mm/contexts/DashboardAiContext";
import { 
  AgenticFeatureRegistry, 
  initializeAnalyticsDashboardProvider 
} from '../ai/chat';
import { defaultTheme } from "react-admin";
import { ReportsSettings } from "./ReportsSettings";
import { useReportInnerTabState } from "./reports-tab-persistence.utils";

// Styled components for top menu tabs - segmented control style
const StyledTopMenuTabsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4f4f5", // muted background
  borderRadius: "8px", // lg border radius
  padding: "4px", // 4px padding
  height: "36px",
  display: "flex",
  alignItems: "center",
  gap: "8px", // 8px gap between tabs
  position: "relative",
  boxShadow: "inset 0px 2px 4px 0px rgba(0,0,0,0.06)", // inner shadow
}));

const StyledTopMenuTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: "transparent !important",
  minHeight: "auto !important",
  height: "100% !important",
  '&.MuiTabs-root': {
    backgroundColor: "transparent !important",
    minHeight: "auto !important",
    height: "100% !important",
  },
  "& .MuiTabs-indicator": {
    display: "none !important",
    height: "0 !important",
  },
  "& .MuiTabs-flexContainer": {
    gap: "8px !important",
    height: "100% !important",
  },
  "& .MuiTabs-scroller": {
    padding: "0 !important",
  },
  "& .MuiTabs-scrollButtons": {
    display: "none !important",
  },
}));

const StyledTopMenuTab = styled(Tab)(({ theme }) => ({
  color: "#71717a", // muted-foreground for inactive
  fontFamily: "'Inter', sans-serif",
  fontWeight: 500, // medium weight
  fontSize: "14px",
  lineHeight: "20px",
  textTransform: "none" as const,
  minHeight: "auto",
  height: "100%",
  padding: "4px 24px", // py-1 (4px) px-6 (24px)
  margin: "0",
  transition: "all 0.2s ease-in-out",
  borderRadius: "6px", // md border radius
  backgroundColor: "transparent",
  border: "none",
  outline: "none",
  '&.MuiTab-root': {
    color: "#71717a",
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
  },
  "&:hover:not(.Mui-disabled)": {
    backgroundColor: "transparent",
    color: "#18181b", // foreground on hover
    border: "none",
    outline: "none",
  },
  "&.Mui-selected": {
    color: "#18181b", // foreground for active
    fontWeight: 500, // medium weight
    backgroundColor: "#ffffff", // white background
    boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.05)", // shadow for active
    border: "none",
    outline: "none",
    '&.MuiTab-root': {
      color: "#18181b",
      backgroundColor: "#ffffff",
      boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.05)",
      border: "none",
      outline: "none",
    },
    '&::before': {
      display: "none",
    },
    '&::after': {
      display: "none",
    },
  },
  "&.Mui-disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  '&::before': {
    display: "none",
  },
  '&::after': {
    display: "none",
  },
}));

// Create light theme for Reports page
const reportsLightTheme = createTheme({
  ...defaultTheme,
  palette: {
    ...defaultTheme.palette,
    mode: 'light',
    primary: {
      main: '#18181b', // Keep black for UI elements
      light: '#27272a',
      dark: '#09090b',
      contrastText: '#fafafa',
    },
    info: {
      main: '#f97316', // Orange-500 (replaces blue)
      light: '#fb923c', // Orange-400
      dark: '#ea580c', // Orange-600
      contrastText: '#ffffff',
    },
    background: {
      default: '#FAFAFA',
      paper: '#ffffff',
    },
  },
  typography: {
    ...defaultTheme.typography,
    fontFamily: "'Inter', sans-serif",
    allVariants: {
      fontFamily: "'Inter', sans-serif",
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif !important",
        },
        h1: {
          fontFamily: "'Inter', sans-serif !important",
        },
        h2: {
          fontFamily: "'Inter', sans-serif !important",
        },
        h3: {
          fontFamily: "'Inter', sans-serif !important",
        },
        h4: {
          fontFamily: "'Inter', sans-serif !important",
        },
        h5: {
          fontFamily: "'Inter', sans-serif !important",
        },
        h6: {
          fontFamily: "'Inter', sans-serif !important",
        },
        body1: {
          fontFamily: "'Inter', sans-serif !important",
        },
        body2: {
          fontFamily: "'Inter', sans-serif !important",
        },
        button: {
          fontFamily: "'Inter', sans-serif !important",
        },
        caption: {
          fontFamily: "'Inter', sans-serif !important",
        },
        overline: {
          fontFamily: "'Inter', sans-serif !important",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          textTransform: 'none' as const,
          letterSpacing: 'normal',
          '&.MuiChip-colorPrimary': {
            backgroundColor: '#18181b',
            color: '#fafafa',
          },
        },
        label: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          textTransform: 'none' as const,
          letterSpacing: 'normal',
        },
        sizeSmall: {
          height: '32px',
          minHeight: '32px',
          maxHeight: '32px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent !important',
          minHeight: 'auto !important',
          '& .MuiTabs-indicator': {
            display: 'none !important',
          },
          '& .MuiTabs-flexContainer': {
            gap: '8px !important',
            borderBottom: 'none !important',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#71717a',
          fontFamily: "'Inter', sans-serif",
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          textTransform: 'none' as const,
          minHeight: 'auto',
          padding: '6px 12px',
          backgroundColor: 'transparent',
          borderBottom: '2px solid transparent',
          borderRadius: '0',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            color: '#18181b',
          },
          '&.Mui-selected': {
            color: '#18181b',
            fontWeight: 400,
            backgroundColor: 'transparent',
            borderBottom: '2px solid #18181b',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif !important",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif !important",
          '& .MuiInputBase-root': {
            fontFamily: "'Inter', sans-serif !important",
          },
          '& .MuiInputLabel-root': {
            fontFamily: "'Inter', sans-serif !important",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif !important",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif !important",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif",
          '&.MuiAlert-standardInfo': {
            backgroundColor: '#fff7ed',
            color: '#9a3412',
            '& .MuiAlert-icon': {
              color: '#f97316',
            },
          },
        },
        message: {
          fontFamily: "'Inter', sans-serif",
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#f97316 !important',
        },
      },
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
      style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {value === index && (
        <Box sx={{ 
          pt: 3, 
          width: '100%', 
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Define all report sections
const REPORT_SECTIONS = [
  { id: 'sales', label: 'Sales', key: 'sales' },
  { id: 'labor', label: 'Labor', key: 'labor' },
  { id: 'menu', label: 'Menu', key: 'menu' },
  { id: 'loyalty', label: 'Loyalty', key: 'loyalty' },
  { id: 'pl', label: 'P&L', key: 'pl' },
  { id: 'settings', label: 'Settings', key: 'settings', icon: true },
];

// Animated Gradient Border - Aceternity UI style
const StarBorder = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '12px',
  padding: '3px',
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  boxSizing: 'border-box',
  background: 'linear-gradient(90deg, #9333ea, #3b82f6, #ec4899, #8b5cf6, #06b6d4, #9333ea, #3b82f6, #ec4899)',
  backgroundSize: '600% 600%',
  animation: 'gradientShift 9s linear infinite',
  '@keyframes gradientShift': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '44.4%': {
      backgroundPosition: '200% 50%',
    },
    '100%': {
      backgroundPosition: '200% 50%',
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '3px',
    borderRadius: '9px',
    backgroundColor: '#09090b',
    zIndex: 0,
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    boxSizing: 'border-box',
  },
}));

// Animated Card Container
const AnimatedCardContainer = styled(Box)(({ theme }) => ({
  animation: 'cardEntry 0.6s ease-out',
  '@keyframes cardEntry': {
    '0%': {
      opacity: 0,
      transform: 'translateX(20px) scale(0.95)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0) scale(1)',
    },
  },
}));

// Insight Card for Drawer - Data-focused
interface InsightCardData {
  id: string;
  title: string;
  description: string;
  badge: string;
  backgroundColor?: string;
  textColor?: string;
  badgeColor?: string;
  badgeTextColor?: string;
  actionButtonBg?: string;
  actionButtonText?: string;
}

const InsightCard: React.FC<{ data: InsightCardData }> = ({ data }) => {
  return (
    <Box
      sx={{
        backgroundColor: data.backgroundColor || '#ffffff',
        border: '1px solid #e4e4e7',
        borderRadius: '12px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', gap: '7px', alignItems: 'center', width: '100%' }}>
        <CakeIcon
          sx={{
            fontSize: '9.375px',
            color: data.textColor === '#fafafa' ? '#a1a1aa' : '#71717a',
            transform: 'rotate(180deg) scaleY(-1)',
          }}
        />
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '18px',
            color: data.textColor === '#fafafa' ? '#a1a1aa' : '#71717a',
            whiteSpace: 'nowrap',
          }}
        >
          CAKE Insight
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          label={data.badge}
          size="small"
          sx={{
            backgroundColor: data.badgeColor || '#f4f4f5',
            color: data.badgeTextColor || '#18181b',
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            padding: '2px 10px',
            borderRadius: '6px',
            border: 'none',
          }}
        />
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 700,
          fontSize: '30px',
          lineHeight: '36px',
          color: data.textColor || '#18181b',
          width: '100%',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {data.title}
      </Typography>

      {/* Description */}
      {data.description && (
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '28px',
            color: data.textColor === '#fafafa' ? '#71717a' : '#71717a',
            width: '100%',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {data.description}
        </Typography>
      )}

      {/* Footer */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        width: '100%',
        minWidth: 0,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: '8px', sm: 0 },
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: '8px', 
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <IconButton
            size="small"
            sx={{
              width: '36px',
              height: '36px',
              border: '1px solid #e4e4e7',
              borderRadius: '6px',
              backgroundColor: data.backgroundColor || '#ffffff',
              color: data.textColor === '#fafafa' ? '#a1a1aa' : '#71717a',
            }}
          >
            <ThumbUpIcon sx={{ fontSize: '16px' }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              width: '36px',
              height: '36px',
              border: '1px solid #e4e4e7',
              borderRadius: '6px',
              backgroundColor: data.backgroundColor || '#ffffff',
              color: data.textColor === '#fafafa' ? '#a1a1aa' : '#71717a',
            }}
          >
            <ThumbDownIcon sx={{ fontSize: '16px' }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              width: '36px',
              height: '36px',
              borderRadius: '6px',
              color: data.textColor === '#fafafa' ? '#a1a1aa' : '#71717a',
            }}
          >
            <BookmarkAddIcon sx={{ fontSize: '16px' }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              width: '36px',
              height: '36px',
              borderRadius: '6px',
              color: data.textColor === '#fafafa' ? '#a1a1aa' : '#71717a',
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            padding: '8px 16px',
            borderRadius: '6px',
            backgroundColor: data.actionButtonBg || '#18181b',
            color: data.actionButtonText || '#fafafa',
            cursor: 'pointer',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '&:hover': {
              opacity: 0.9,
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            }}
          >
            Take Action
          </Typography>
          <ArrowUpwardIcon sx={{ fontSize: '16px', transform: 'rotate(45deg)' }} />
        </Box>
      </Box>
    </Box>
  );
};

// Insights Drawer Component - AI Assistant Interface
const InsightsDrawer: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [chatInput, setChatInput] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speechSynthesisRef = React.useRef<SpeechSynthesis | null>(null);

  const suggestedQuestions = [
    'What factors contributed to the revenue increase?',
    'How did online orders impact growth?',
    'What operational changes were most effective?',
    'Compare performance across different time periods',
  ];

  const suggestedTopics = [
    'Revenue trends',
    'Order volume analysis',
    'Average ticket size',
    'Peak hours performance',
  ];

  // Answer text to be read aloud
  const answerText = `Based on data from CAKE POS Terminals, CAKEpop devices, and online orders, Ike's – Mission Rock realized a 4.1x increase in average monthly revenue between Q1 and Q4. Transaction data indicated a 62% rise in order volume and a 25% uplift in average ticket size. Operational metrics showed a 37% decrease in average order time after handheld adoption and a 42% increase in table turnover during peak hours. Online orders contributed 31% of total revenue by Q4, driven by targeted digital promotions and integrated loyalty campaigns. Labor scheduling adjustments informed by CAKE Analytics reduced overtime costs by 18% while maintaining service consistency. Combined, these efficiencies and revenue gains demonstrate how data-led optimization and digital integration translated directly into sustained financial growth within twelve months.`;

  const handleSpeak = () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const synth = window.speechSynthesis;
      
      if (isSpeaking) {
        // Stop speaking
        synth.cancel();
        setIsSpeaking(false);
      } else {
        // Start speaking
        synth.cancel(); // Cancel any ongoing speech
        
        const utterance = new SpeechSynthesisUtterance(answerText);
        utterance.rate = 1.0; // Normal speed
        utterance.pitch = 1.0; // Normal pitch
        utterance.volume = 1.0; // Full volume
        
        utterance.onstart = () => {
          setIsSpeaking(true);
        };
        
        utterance.onend = () => {
          setIsSpeaking(false);
        };
        
        utterance.onerror = () => {
          setIsSpeaking(false);
        };
        
        synth.speak(utterance);
      }
    }
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '440px', md: '440px' },
          height: '100vh',
          boxSizing: 'border-box',
          backgroundColor: '#18181b',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#18181b',
          position: 'relative',
        }}
      >
        {/* Header with action buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid #27272a',
            backgroundColor: '#18181b',
          }}
        >
          <Box sx={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
            <IconButton
              size="small"
              sx={{
                color: '#fafafa',
                padding: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <PushPinIcon sx={{ fontSize: '24px' }} />
            </IconButton>
            <IconButton
              size="small"
              sx={{
                color: '#fafafa',
                padding: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <ShareIcon sx={{ fontSize: '24px' }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleSpeak}
              sx={{
                color: isSpeaking ? '#f97316' : '#fafafa',
                padding: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <VolumeUpIcon sx={{ fontSize: '24px' }} />
            </IconButton>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#fafafa',
              padding: '8px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '24px' }} />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 24px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '48px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* Answer Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#a1a1aa',
              }}
            >
              Answer
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '24px',
                lineHeight: '32px',
                color: '#fafafa',
              }}
            >
              How did Ike's - Mission Rock quadruple monthly revenue in less than one year?
            </Typography>
            <Box
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                color: '#a1a1aa',
              }}
            >
              <Typography
                component="p"
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#a1a1aa',
                  marginBottom: '16px',
                }}
              >
                Based on data from CAKE POS Terminals, CAKEpop devices, and online orders, Ike's – Mission Rock realized a 4.1x increase in average monthly revenue between Q1 and Q4. Transaction data indicated a 62% rise in order volume and a 25% uplift in average ticket size.
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#a1a1aa',
                  marginBottom: '16px',
                }}
              >
                Operational metrics showed a 37% decrease in average order time after handheld adoption and a 42% increase in table turnover during peak hours. Online orders contributed 31% of total revenue by Q4, driven by targeted digital promotions and integrated loyalty campaigns.
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#a1a1aa',
                }}
              >
                Labor scheduling adjustments informed by CAKE Analytics reduced overtime costs by 18% while maintaining service consistency. Combined, these efficiencies and revenue gains demonstrate how data-led optimization and digital integration translated directly into sustained financial growth within twelve months.
              </Typography>
            </Box>

            {/* Chart Card */}
            <Box
              sx={{
                backgroundColor: '#09090b',
                border: '1px solid #27272a',
                borderRadius: '8px',
                padding: '24px',
                marginTop: '24px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '20px',
                  color: '#fafafa',
                  marginBottom: '24px',
                }}
              >
                Food vs Beverage comparison
              </Typography>
              {/* Placeholder for chart - would integrate actual chart component here */}
              <Box
                sx={{
                  height: '183px',
                  backgroundColor: '#18181b',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#71717a',
                  fontSize: '14px',
                }}
              >
                Chart visualization
              </Box>
            </Box>

            {/* Stats Cards */}
            <Box
              sx={{
                backgroundColor: '#09090b',
                border: '1px solid #27272a',
                borderRadius: '12px',
                padding: '24px',
                display: 'flex',
                gap: '10px',
                marginTop: '24px',
              }}
            >
              {/* Average Check Size */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#a1a1aa',
                    }}
                  >
                    Average Check Size
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#16a34a',
                    }}
                  >
                    +15.11%
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: '30px',
                    lineHeight: '36px',
                    color: '#fafafa',
                  }}
                >
                  $12.05
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Suggested Questions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#a1a1aa',
              }}
            >
              Suggested questions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {suggestedQuestions.map((question, index) => (
                <Box
                  key={index}
                  onClick={() => setChatInput(question)}
                  sx={{
                    padding: '12px 16px',
                    backgroundColor: '#09090b',
                    border: '1px solid #27272a',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#27272a',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#fafafa',
                    }}
                  >
                    {question}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Suggested Topics */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#a1a1aa',
              }}
            >
              Suggested topics
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {suggestedTopics.map((topic, index) => (
                <Chip
                  key={index}
                  label={topic}
                  onClick={() => setChatInput(`Tell me about ${topic.toLowerCase()}`)}
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '16px',
                    backgroundColor: '#09090b',
                    color: '#fafafa',
                    border: '1px solid #27272a',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#27272a',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Chat Input - Sticky at bottom */}
        <Box
          sx={{
            padding: '16px',
            backgroundColor: '#18181b',
            borderTop: '1px solid #27272a',
            boxShadow: '0px -4px 4px 0px rgba(0,0,0,0.25)',
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              backgroundColor: '#a1a1aa',
              border: '1px solid #fafafa',
              borderRadius: '8px',
              padding: '0 14px',
              height: '55px',
            }}
          >
            <TextField
              fullWidth
              placeholder="Ask a follow up..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && chatInput.trim()) {
                  // Handle send message
                  setChatInput('');
                }
              }}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 400,
                  fontSize: '18px',
                  lineHeight: '0',
                  color: '#27272a',
                  '&::placeholder': {
                    color: '#27272a',
                    opacity: 1,
                  },
                },
              }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  height: '55px',
                },
              }}
            />
            <IconButton
              sx={{
                backgroundColor: '#a1a1aa',
                color: '#27272a',
                padding: '8px 12px',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#71717a',
                },
              }}
            >
              <FlashOnIcon sx={{ fontSize: '14.4px' }} />
              <KeyboardArrowDownIcon sx={{ fontSize: '14.4px', ml: '3px' }} />
            </IconButton>
            <IconButton
              sx={{
                border: '0.611px solid #27272a',
                borderRadius: '18.333px',
                width: '32px',
                height: '32px',
                padding: '4.889px',
                color: '#27272a',
                '&:hover': {
                  backgroundColor: 'rgba(39, 39, 42, 0.1)',
                },
              }}
            >
              <MicIcon sx={{ fontSize: '13.037px' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

// Insights Card Component
const InsightsCard: React.FC<{ inRow?: boolean; onClick?: () => void }> = ({ inRow = false, onClick }) => {
  return (
    <AnimatedCardContainer
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        ...(onClick && {
          '&:hover': {
            transform: 'scale(1.02)',
            transition: 'transform 0.2s ease',
          },
        }),
        ...(inRow ? {
          flex: { xs: '1 1 100%', md: '1 1 0%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          height: '122px',
          width: { xs: '100%', md: 'auto' },
          minWidth: { xs: 0, md: 0 },
          maxWidth: { xs: '100%', md: '100%' },
          overflow: 'hidden',
          boxSizing: 'border-box',
        } : {
          position: 'fixed',
          top: 16,
          right: 24,
          zIndex: 1200,
          width: '320px',
          maxWidth: 'calc(100vw - 48px)',
        }),
      }}
    >
      <StarBorder>
        <Box
          sx={{
            backgroundColor: '#09090b',
            border: 'none',
            borderRadius: '12px',
            padding: inRow ? '8px' : '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: inRow ? '8px' : '16px',
            height: inRow ? '100%' : 'auto',
            overflow: 'hidden',
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            gap: '7px', 
            alignItems: 'center', 
            width: '100%',
            minWidth: 0,
            flexShrink: 0,
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <AutoAwesomeIcon
                sx={{
                  fontSize: '15px',
                  color: '#f9fafb',
                  transform: 'rotate(180deg) scaleY(-1)',
                }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '18px',
                color: '#a1a1aa',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              CAKE Data Insight
            </Typography>
          </Box>

          {/* Content */}
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: inRow ? '12px' : '16px',
              lineHeight: inRow ? '1.4' : '1.3',
              color: '#fafafa',
              flex: '1 1 auto',
              minWidth: 0,
              ...(inRow && {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }),
            }}
          >
            Ike's - Mission Rock has quadrupled its monthly revenue in less than a year.
          </Typography>

          {/* Footer */}
          <Box sx={{ 
            display: 'flex', 
            gap: '8px', 
            alignItems: 'center', 
            justifyContent: 'flex-end', 
            width: '100%',
            minWidth: 0,
            flexShrink: 0,
          }}>
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                padding: inRow ? '4px 8px' : '8px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: inRow ? '11px' : '12px',
                  lineHeight: '16px',
                  color: '#fafafa',
                  whiteSpace: 'nowrap',
                }}
              >
                Learn Why
              </Typography>
              <ArrowForwardIcon
                sx={{
                  fontSize: inRow ? '14px' : '16px',
                  color: '#fafafa',
                  flexShrink: 0,
                }}
              />
            </Box>
          </Box>
        </Box>
      </StarBorder>
    </AnimatedCardContainer>
  );
};

export const Reports = () => {
  const dataProvider = useDataProvider();
  const [dashboards, setDashboards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [insightsDrawerOpen, setInsightsDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Map dashboards to sections
  const getDashboardForSection = (sectionKey: string) => {
    return dashboards.find(d => d.key?.toLowerCase().includes(sectionKey.toLowerCase()));
  };
  
  // Check if a section is enabled (has dashboard data)
  const isSectionEnabled = (sectionKey: string) => {
    if (sectionKey === 'settings') return true; // Settings tab is always enabled
    return getDashboardForSection(sectionKey) !== undefined;
  };
  
  // Get current dashboard based on active tab
  const currentDashboard = activeTabIndex < REPORT_SECTIONS.length 
    ? getDashboardForSection(REPORT_SECTIONS[activeTabIndex].key)
    : null;
  
  // Get the current section key and manage inner tab state for it
  const currentSectionKey = REPORT_SECTIONS[activeTabIndex]?.key || '';
  const [innerTabId, setInnerTabId] = useReportInnerTabState(
    currentSectionKey,
    currentDashboard?.currentConfig?.config?.tabs?.[0]?.id
  );

  // Initialize analytics dashboard feature context when component mounts
  useEffect(() => {
    console.log('🎯 [Reports View] Initializing AI feature context...');
    
    // Initialize the analytics dashboard provider (registers event listeners)
    initializeAnalyticsDashboardProvider();
    
    // Note: Feature context is now managed by route-based context in App.tsx
    // This component only manages focusKey for the active dashboard
  }, []);

  // Fetch all analytics dashboards and trigger preloading
  useEffect(() => {
    const fetchDashboards = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await dataProvider.getList('analytics-dashboards', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { published: true }
        });

        // Filter to only dashboards with valid configurations
        const validDashboards = data.filter(
          (dashboard: any) => dashboard.currentConfig?.config
        );

        setDashboards(validDashboards);

        if (validDashboards.length === 0) {
          setError('No published reports dashboards found. Please create and publish dashboards in the admin section.');
        }
      } catch (err: any) {
        console.error('Error fetching reports dashboards:', err);
        setError(err.message || 'Failed to load reports dashboards');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboards();
  }, [dataProvider]);

  // Set focus when active dashboard changes
  useEffect(() => {
    if (currentDashboard) {
      const dashboardId = String(currentDashboard.id || currentDashboard['analytics-dashboardsId']);
      console.log(`🎯 [Reports View] Setting focus to dashboard: ${dashboardId}`);
      AgenticFeatureRegistry.setFocusKey(dashboardId);
    }
  }, [activeTabIndex, currentDashboard]);
  
  // Format date range for display
  const formatDateRange = (start: Date, end: Date) => {
    // Validate dates before formatting
    if (!start || !end || !(start instanceof Date) || !(end instanceof Date)) {
      return 'Select date range';
    }
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Select date range';
    }
    
    const formatDate = (date: Date) => {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[date.getMonth()]} ${date.getDate()}`;
    };
    return `${formatDate(start)} – ${formatDate(end)}, ${end.getFullYear()}`;
  };

  // Format date range summary (e.g., "Last 7 days", "Last 30 days")
  const formatDateRangeSummary = (start: Date, end: Date): string => {
    if (!start || !end || !(start instanceof Date) || !(end instanceof Date) || isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Last 7 days'; // Default fallback
    }
    
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    // Check for common ranges
    if (daysDiff === 1) {
      return 'Today';
    } else if (daysDiff === 7 || (daysDiff >= 6 && daysDiff <= 8)) {
      return 'Last 7 days';
    } else if (daysDiff === 30 || (daysDiff >= 28 && daysDiff <= 32)) {
      return 'Last 30 days';
    } else if (daysDiff === 90 || (daysDiff >= 88 && daysDiff <= 92)) {
      return 'Last 90 days';
    } else if (daysDiff === 365 || (daysDiff >= 363 && daysDiff <= 367)) {
      return 'Last year';
    } else {
      return `${daysDiff} days`;
    }
  };

  // Resolve date value (handles date constants)
  const resolveDateValue = (dateValue: any): Date | null => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue === 'string') {
      // Check if it's a date constant
      try {
        const { DateConstantsService } = require('../analytics/mm/services/date-constants.service');
        if (DateConstantsService.isDateConstant(dateValue)) {
          const resolvedDateString = DateConstantsService.resolveDateConstant(dateValue);
          return new Date(resolvedDateString);
        }
      } catch (e) {
        // DateConstantsService not available, fall through
      }
      return new Date(dateValue);
    }
    return null;
  };
  
  // Default to last 7 days
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return { start, end };
  });

  // Store filter values to match chip
  const [chipFilterValues, setChipFilterValues] = React.useState<Record<string, any>>({});
  const [chipFilters, setChipFilters] = React.useState<any[]>([]);
  
  // Handle filter values change from dashboard - store them to match chip
  const handleFilterValuesChange = (filterValues: Record<string, any>) => {
    if (filterValues && Object.keys(filterValues).length > 0) {
      setChipFilterValues(filterValues);
    }
    // Store filters from dashboard (same as dynamicFilters passed to GlobalFilterPanel)
    if (currentDashboard?.currentConfig?.config?.filters) {
      setChipFilters(currentDashboard.currentConfig.config.filters);
    }
  };
  
  // Force sync date range with chip - uses EXACT same logic as GlobalFilterPanel.getFilterSummary
  useEffect(() => {
    if (!chipFilterValues || Object.keys(chipFilterValues).length === 0 || chipFilters.length === 0) {
      return; // Wait for chip values
    }
    
    // Process filters EXACTLY like GlobalFilterPanel.getFilterSummary does
    chipFilters.forEach((filter: any) => {
      // Skip location filter (same as GlobalFilterPanel)
      const isLocationFilter = filter.label?.toLowerCase().includes('location') || 
                                filter.filterId?.toLowerCase().includes('location');
      if (isLocationFilter) return;
      
      // EXACT same logic as GlobalFilterPanel chip: const value = filterValues[filter.filterId];
      const value = chipFilterValues[filter.filterId];
      
      // EXACT same check as GlobalFilterPanel chip: if (value === undefined || value === null) return;
      if (value === undefined || value === null) return;
      
      if (filter.type === 'date-range') {
        // EXACT same check as GlobalFilterPanel chip: if (value?.start || value?.end)
        if (value?.start || value?.end) {
          // EXACT same resolveDateValue logic as GlobalFilterPanel
          const startDate = resolveDateValue(value.start);
          const endDate = resolveDateValue(value.end);
          
          // FORCE update to match chip - same dates chip displays
          if (startDate && endDate && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            setDateRange({ start: startDate, end: endDate });
          }
        }
      }
    });
  }, [chipFilterValues, chipFilters]);

  // Handle scroll detection for glass effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY || document.documentElement.scrollTop;
      setIsScrolled(scrollPosition > 0);
    };

    // Check initial scroll position
    handleScroll();

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTabIndex(newValue);
  };

  if (loading) {
    return (
      <ThemeProvider theme={reportsLightTheme}>
        <Box
          sx={{
            width: "100%",
            height: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "background.default",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ mt: 2, color: "text.primary" }}>
              Loading Reports Dashboards...
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={reportsLightTheme}>
        <Box sx={{ width: "100%", padding: 3, backgroundColor: "background.default" }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Please ensure reports dashboards are configured in the admin section.
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (dashboards.length === 0) {
    return (
      <ThemeProvider theme={reportsLightTheme}>
        <Box sx={{ width: "100%", padding: 3, backgroundColor: "background.default" }}>
          <Typography variant="h4" gutterBottom sx={{ color: "text.primary" }}>
            Reports Dashboards
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            No published reports dashboards available yet. Please create and publish dashboards in the
            admin section to view them here.
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={reportsLightTheme}>
      <Box 
        sx={{ 
          width: "100%", 
          height: "100%",
          backgroundColor: "background.default",
          overflowX: 'hidden', // Prevent horizontal overflow
          maxWidth: 'calc(100vw - 66px)', // Subtract sidebar width (50px)
          boxSizing: 'border-box',
        }}
      >

        {/* Header Row with Title, Tabs, and Controls */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1100,
            backgroundColor: isScrolled ? "rgba(255, 255, 255, 0.8)" : "transparent",
            backdropFilter: isScrolled ? "blur(12px)" : "none",
            WebkitBackdropFilter: isScrolled ? "blur(12px)" : "none",
            borderBottom: isScrolled ? "1px solid rgba(255, 255, 255, 0.2)" : "none",
            boxShadow: isScrolled ? "0 1px 3px rgba(0, 0, 0, 0.05)" : "none",
            transition: "background-color 0.3s ease, backdrop-filter 0.3s ease, border-bottom 0.3s ease, box-shadow 0.3s ease",
          }}
        >
          <Box
            sx={{
              px: 3, // 24px padding
              py: 2, // 16px padding
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* Reports Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 700, // bold
                  fontSize: "30px",
                  lineHeight: "36px",
                  color: "#09090b", // card-foreground
                  letterSpacing: "-0.4px",
                  whiteSpace: "nowrap",
                }}
              >
                Reports
              </Typography>
              <Chip
                label="BETA"
                size="small"
                sx={{
                  height: "20px",
                  fontSize: "10px",
                  fontWeight: 600,
                  lineHeight: "14px",
                  padding: "2px 6px",
                  backgroundColor: "#f4f4f5",
                  color: "#71717a",
                  border: "1px solid #e4e4e7",
                  borderRadius: "4px",
                  fontFamily: "'Inter', sans-serif",
                  "& .MuiChip-label": {
                    padding: "0 4px",
                  },
                }}
              />
            </Box>

            {/* Tab Group - Centered */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                px: 6.625, // 53px padding
              }}
            >
              <StyledTopMenuTabsContainer>
                <StyledTopMenuTabs
                  value={activeTabIndex}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {REPORT_SECTIONS.map((section, index) => (
                    <StyledTopMenuTab
                      key={section.id}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {section.icon && (
                            <SettingsIcon sx={{ fontSize: "16px" }} />
                          )}
                          <span>{section.label}</span>
                        </Box>
                      }
                      disabled={!isSectionEnabled(section.key)}
                    />
                  ))}
                </StyledTopMenuTabs>
              </StyledTopMenuTabsContainer>
            </Box>

            {/* Right Side Controls */}
            <Box
              sx={{
                display: "none", // Hidden
                gap: 1, // 8px gap
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {/* Badge - Dynamic date range summary */}
              <Chip
                label={formatDateRangeSummary(dateRange.start, dateRange.end)}
                sx={{
                  backgroundColor: "#18181b", // foreground
                  color: "#f4f4f5", // accent
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600, // semibold
                  fontSize: "12px",
                  lineHeight: "16px",
                  height: "auto",
                  padding: "2px 10px",
                  borderRadius: "6px",
                  border: "1px solid transparent",
                }}
              />

              {/* Date Picker */}
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e4e4e7", // input border
                  borderRadius: "6px",
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 16px",
                  gap: 1, // 8px gap
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "#18181b",
                  },
                }}
                onClick={() => {
                  // TODO: Open date picker dialog
                  console.log("Date picker clicked");
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: "16px", color: "#18181b" }} />
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 400, // normal
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#18181b", // foreground
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDateRange(dateRange.start, dateRange.end)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Dashboard Content */}
        {REPORT_SECTIONS[activeTabIndex]?.key === 'settings' ? (
          <TabPanel value={activeTabIndex} index={activeTabIndex}>
            <Box sx={{ width: "100%", padding: 3 }}>
              <ReportsSettings />
            </Box>
          </TabPanel>
        ) : currentDashboard ? (
          <TabPanel value={activeTabIndex} index={activeTabIndex}>
          <DashboardAiProvider applicationKey={currentDashboard.key}>
              <TabbedAnalyticsDashboard
                config={currentDashboard.currentConfig.config}
                dashboardKey={currentDashboard.key}
                defaultTab={innerTabId || undefined}
                enableLegacyFallback={false}
                showHeader={false}
                initialUseLiveData={true}
                onTabChange={(tabId) => {
                  setInnerTabId(tabId);
                }}
                onFilterValuesChange={handleFilterValuesChange}
              />
            </DashboardAiProvider>
          </TabPanel>
        ) : (
          <Box sx={{ width: "100%", padding: 3, backgroundColor: "background.default" }}>
            <Alert severity="warning" sx={{ mt: 2 }}>
              No dashboard available for this section. Please configure a dashboard in the admin section.
            </Alert>
          </Box>
        )}
      </Box>

      {/* Insights Drawer */}
      <InsightsDrawer 
        open={insightsDrawerOpen} 
        onClose={() => setInsightsDrawerOpen(false)} 
      />
    </ThemeProvider>
  );
};

export default Reports;

